// Typed client for subscription billing.
//
//   GET  /api/Billing/config        — is billing on, and the publishable key (anonymous)
//   GET  /api/Billing/subscription  — the caller's current state
//   POST /api/Billing/checkout      — start a purchase, returns a Stripe URL to redirect to
//   POST /api/Billing/portal        — manage/cancel, returns a Stripe URL
//
// There is no "confirm payment" call and there shouldn't be: entitlement is granted by the
// Stripe webhook, never by the browser coming back to a success URL. Returning from checkout
// only means the user finished the form.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export interface BillingConfig {
  enabled: boolean;
  publishableKey: string | null;
}

export interface SubscriptionSnapshot {
  billingEnabled: boolean;
  planId: string;
  planName: string;
  planRank: number;
  /** Raw Stripe status: active, trialing, past_due, canceled… null when never subscribed. */
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasCustomer: boolean;
}

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.error ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export const billingService = {
  config: async (): Promise<BillingConfig> => {
    const res = await apiFetch(`${API_BASE}/api/Billing/config`);
    if (!res.ok) return { enabled: false, publishableKey: null };
    return res.json() as Promise<BillingConfig>;
  },

  subscription: async (): Promise<SubscriptionSnapshot> => {
    const res = await apiFetch(`${API_BASE}/api/Billing/subscription`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<SubscriptionSnapshot>;
  },

  /**
   * Begin a purchase and hand back the Stripe Checkout URL.
   *
   * `returnUrlBase` is the caller's own origin, used only when the server has no configured
   * base — the server prefers its own and ignores this, which is what stops a crafted request
   * turning the payment flow into an open redirect.
   */
  checkout: async (planId: string): Promise<string> => {
    const res = await apiFetch(`${API_BASE}/api/Billing/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId, returnUrlBase: window.location.origin }),
    });
    if (!res.ok) throw new Error(await readError(res));
    const body = (await res.json()) as { url: string };
    return body.url;
  },

  portal: async (): Promise<string> => {
    const res = await apiFetch(`${API_BASE}/api/Billing/portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnUrlBase: window.location.origin }),
    });
    if (!res.ok) throw new Error(await readError(res));
    const body = (await res.json()) as { url: string };
    return body.url;
  },
};
